import { z } from 'zod';

import { CLAIM_FMI_STATUS } from '@/lib/data/domains/claim/constant';
import { USER_TYPE } from '@/lib/data/domains/user';

export const ClaimCreateFormSchema = z
  .object({
    userRole: z.enum([USER_TYPE.FABRICATOR, USER_TYPE.FMI]),
    id: z.string(),
    action: z.enum(['saveProgress', 'send']),
    customer: z.object({
      id: z.string(),
      name: z.string(),
    }),
    dateOfClaim: z.number(),
    fabricator: z.object({
      id: z.string(),
      name: z.string(),
    }),
    fabricatorJobReference: z.string(),
    fmiWorksOrderNumber: z.string(),
    originalInstallDate: z.number().nullable(),
    glazed: z.object({
      id: z.string(),
      name: z.string(),
    }),
    inspectedBy: z.string(),
    address: z.object({
      placeId: z.string(),
      address: z.string(),
    }),
    contactNumber: z.string(),
    contactEmail: z.string(),
    splitPdfTasks: z.array(
      z.object({
        id: z.string(),
        pages: z.array(z.unknown()),
      }),
    ),
    fabricatorAdditionalNotes: z.string(),
    glazier: z.object({
      id: z.string(),
      name: z.string(),
    }),
  })
  .superRefine((data, ctx: any) => {
    if (!data.customer.id) {
      ctx.addIssue({
        path: ['customer'],
        message: 'Customer name is required',
        code: 'custom',
      });
    }

    if (!data.fabricator.id) {
      ctx.addIssue({
        path: ['fabricator'],
        message: 'Fabricator is required',
        code: 'custom',
      });
    }

    if (!data.glazed.id) {
      ctx.addIssue({
        path: ['glazed'],
        message: 'Glazed is required',
        code: 'custom',
      });
    }

    if (!data.address.placeId) {
      ctx.addIssue({
        path: ['address'],
        message: 'Address is required',
        code: 'custom',
      });
    }

    if (
      !data.fabricatorJobReference ||
      data.fabricatorJobReference.length > 200
    ) {
      ctx.addIssue({
        path: ['fabricatorJobReference'],
        message: !data.fabricatorJobReference
          ? 'Fabricator job reference is required'
          : 'Maximum 200 characters',
        code: 'custom',
      });
    }

    if (!data.fmiWorksOrderNumber || data.fmiWorksOrderNumber.length > 200) {
      ctx.addIssue({
        path: ['fmiWorksOrderNumber'],
        message: !data.fmiWorksOrderNumber
          ? 'FMI works order number is required'
          : 'Maximum 200 characters',
        code: 'custom',
      });
    }

    if (!data.originalInstallDate) {
      ctx.addIssue({
        path: ['originalInstallDate'],
        message: 'Original install date is required',
        code: 'custom',
      });
    }

    if (data.inspectedBy.length > 100) {
      ctx.addIssue({
        path: ['inspectedBy'],
        message: 'Maximum 100 characters',
        code: 'custom',
      });
    }

    if (!data.contactNumber || data.contactNumber.length > 200) {
      ctx.addIssue({
        path: ['contactNumber'],
        message: !data.contactNumber
          ? 'Contact number is required'
          : 'Maximum 200 characters',
        code: 'custom',
      });
    }

    if (
      data.contactEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.contactEmail.trim())
    ) {
      ctx.addIssue({
        path: ['contactEmail'],
        message: 'Please enter a valid email',
        code: 'custom',
      });
    }

    if (
      data.userRole === USER_TYPE.FABRICATOR &&
      data.action === 'send' &&
      ((data.splitPdfTasks || []).length === 0 ||
        !data.splitPdfTasks[0].pages.some((page: any) => page.selected))
    ) {
      ctx.addIssue({
        path: ['splitPdfTasks'],
        message: 'Must contain at least one claim documentation',
        code: 'custom',
      });
    }
  });

export const ClaimCustomerDetailsFormSchema = z
  .object({
    unitsAffected: z.number().optional().nullable(),
    unitsFailed: z.number().optional().nullable(),
    truckAccess: z.boolean(),
    offStreetParking: z.boolean(),
    safeOffloadArea: z.boolean(),
    helpOnSite: z.boolean(),
    groundFloor: z.boolean(),
    additionalNotes: z.string(),
    fabricatorAdditionalNotes: z.string(),
    drawings: z.array(z.unknown()),
    photos: z.array(z.unknown()),
    glazier: z.object({
      id: z.string(),
      name: z.string(),
    }),
  })
  .superRefine((data, ctx: any) => {
    if (!data.unitsAffected) {
      ctx.addIssue({
        path: ['unitsAffected'],
        message: 'Total number of unit(s) affected is required',
        code: 'custom',
      });
    }

    if (!data.unitsFailed) {
      ctx.addIssue({
        path: ['unitsFailed'],
        message: 'Total number of failed unit(s) only is required',
        code: 'custom',
      });
    }

    if (
      data.unitsAffected != null &&
      data.unitsFailed != null &&
      data.unitsFailed > data.unitsAffected
    ) {
      ctx.addIssue({
        path: ['unitsFailed'],
        message: 'Failed units cannot be greater than affected units',
        code: 'custom',
      });
    }

    if (data.photos.length < 1) {
      ctx.addIssue({
        path: ['photos'],
        message: 'Must contain at least one photo',
        code: 'custom',
      });
    }

    if (data.unitsFailed != null && data.photos.length < data.unitsFailed) {
      ctx.addIssue({
        path: ['photos'],
        message:
          'Number of photos must be at least the total number of failed units',
        code: 'custom',
      });
    }

    if (
      data.drawings.length > 0 &&
      data.drawings.every((drawing: any) => !drawing.isMarked)
    ) {
      ctx.addIssue({
        path: ['drawings'],
        message: 'Must contain at least one mark document',
        code: 'custom',
      });
    }
  });

export const ClaimFeedbackFormSchema = z
  .object({
    score: z.number().nullable(),
    additionalFeedback: z.string(),
  })
  .superRefine((data, ctx) => {
    if (!data.score || data.score < 1) {
      ctx.addIssue({
        path: ['score'],
        message: 'Rating is required',
        code: 'custom',
      });
    }
  });

export const ClaimFMIDetailsFormSchema = z
  .object({
    glazier: z.object({
      id: z.string(),
      name: z.string(),
    }),
    d365OrderNumber: z.string(),
    replacementD365Number: z.string(),
    fmiCostsFiles: z.array(z.unknown()),
    originalFmiCosts: z.number().optional().nullable(),
    d365ReplacementCosts: z.number().optional().nullable(),
    internalNotes: z.string(),
    statusInformation: z.object({
      comment: z.string(),
    }),
    glassFaultDetected: z.array(z.string()),
    otherFaultDescription: z.string(),
  })
  .superRefine((data, ctx: any) => {
    if (!data.d365OrderNumber) {
      ctx.addIssue({
        path: ['d365OrderNumber'],
        message: 'D365 Order number is required',
        code: 'custom',
      });
    }

    if (!data.glazier.id) {
      ctx.addIssue({
        path: ['glazier'],
        message: 'Please assign a glazier before accepting this claim.',
        code: 'custom',
      });
    }

    if (data.glassFaultDetected.length === 0) {
      ctx.addIssue({
        path: ['glassFaultDetected'],
        message: 'You have to select at least one fault',
        code: 'custom',
      });
    }

    if (
      data.glassFaultDetected.some((x: string) => x === 'otherFault') &&
      (data.otherFaultDescription || '').length === 0
    ) {
      ctx.addIssue({
        path: ['otherFaultDescription'],
        message: 'Other note is required',
        code: 'custom',
      });
    }
  });

export const ClaimDeclineFormSchema = z
  .object({
    reason: z.string(),
  })
  .superRefine((data, ctx) => {
    if (!data.reason) {
      ctx.addIssue({
        path: ['reason'],
        message: 'Reason is required',
        code: 'custom',
      });
    }
  });

export const ClaimRequestMoreInfoFormSchema = z
  .object({
    reason: z.string(),
  })
  .superRefine((data, ctx) => {
    if (!data.reason) {
      ctx.addIssue({
        path: ['reason'],
        message: 'Reason is required',
        code: 'custom',
      });
    }
  });

export const ClaimFMIEditFormSchema = z
  .object({
    id: z.string(),
    status: z.string(),
    customer: z.object({
      name: z.string(),
      id: z.string(),
    }),
    dateOfClaim: z.number(),
    fabricator: z.object({
      name: z.string(),
      id: z.string(),
    }),
    fabricatorJobReference: z.string(),
    fmiWorksOrderNumber: z.string(),
    originalInstallDate: z.number().nullable(),
    glazed: z.object({
      name: z.string(),
      id: z.string(),
    }),
    inspectedBy: z.string(),
    address: z.object({
      placeId: z.string(),
      address: z.string(),
    }),
    contactNumber: z.string(),
    contactEmail: z.string(),
    unitsAffected: z.number().optional().nullable(),
    unitsFailed: z.number().optional().nullable(),
    truckAccess: z.boolean(),
    offStreetParking: z.boolean(),
    safeOffloadArea: z.boolean(),
    helpOnSite: z.boolean(),
    groundFloor: z.boolean(),
    additionalNotes: z.string(),
  })
  .superRefine((data, ctx: any) => {
    if (!data.customer.id) {
      ctx.addIssue({
        path: ['customer'],
        message: 'Customer name is required',
        code: 'custom',
      });
    }

    if (!data.fabricator.id) {
      ctx.addIssue({
        path: ['fabricator'],
        message: 'Fabricator is required',
        code: 'custom',
      });
    }

    if (!data.glazed.id) {
      ctx.addIssue({
        path: ['glazed'],
        message: 'Glazed is required',
        code: 'custom',
      });
    }

    if (!data.address.placeId) {
      ctx.addIssue({
        path: ['address'],
        message: 'Address is required',
        code: 'custom',
      });
    }

    if (
      !data.fabricatorJobReference ||
      data.fabricatorJobReference.length > 200
    ) {
      ctx.addIssue({
        path: ['fabricatorJobReference'],
        message: !data.fabricatorJobReference
          ? 'Fabricator job reference is required'
          : 'Maximum 200 characters',
        code: 'custom',
      });
    }

    if (!data.fmiWorksOrderNumber || data.fmiWorksOrderNumber.length > 200) {
      ctx.addIssue({
        path: ['fmiWorksOrderNumber'],
        message: !data.fmiWorksOrderNumber
          ? 'FMI works order number is required'
          : 'Maximum 200 characters',
        code: 'custom',
      });
    }

    if (!data.originalInstallDate) {
      ctx.addIssue({
        path: ['originalInstallDate'],
        message: 'Original install date is required',
        code: 'custom',
      });
    }

    if (data.inspectedBy.length > 100) {
      ctx.addIssue({
        path: ['inspectedBy'],
        message: 'Maximum 100 characters',
        code: 'custom',
      });
    }

    if (!data.contactNumber || data.contactNumber.length > 200) {
      ctx.addIssue({
        path: ['contactNumber'],
        message: !data.contactNumber
          ? 'Contact number is required'
          : 'Maximum 200 characters',
        code: 'custom',
      });
    }

    if (
      data.contactEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.contactEmail.trim())
    ) {
      ctx.addIssue({
        path: ['contactEmail'],
        message: 'Please enter a valid email',
        code: 'custom',
      });
    }

    const isClaimStatusSent =
      data.status === CLAIM_FMI_STATUS.SENDING_TO_CUSTOMER ||
      data.status === CLAIM_FMI_STATUS.SENT_TO_CUSTOMER ||
      data.status === CLAIM_FMI_STATUS.ADDITIONAL_INFORMATION_REQUESTED;

    if (!isClaimStatusSent) {
      if (!data.unitsAffected) {
        ctx.addIssue({
          path: ['unitsAffected'],
          message: 'Total number of unit(s) affected is required',
          code: 'custom',
        });
      }

      if (!data.unitsFailed) {
        ctx.addIssue({
          path: ['unitsFailed'],
          message: 'Total number of failed unit(s) only is required',
          code: 'custom',
        });
      }

      if (
        data.unitsAffected != null &&
        data.unitsFailed != null &&
        data.unitsFailed > data.unitsAffected
      ) {
        ctx.addIssue({
          path: ['unitsFailed'],
          message: 'Failed units cannot be greater than affected units',
          code: 'custom',
        });
      }

    }
  });
